import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import Loader from '../common/Loader';
import './Settings.css';

const SETTINGS_KEYS = [
  'database_url_secret',
  'video_generator_api_url',
  'voice_api_url',
  'video_generator_timeout',
  'gcp_bucket_name',
  'default_video_processing_time',
  'max_retries',
  'default_priority',
  'credit_formula_slope',
  'credit_formula_intercept',
  'processing_time_intercept',
  'processing_time_text_len_coeff',
  'processing_time_sampling_ts_coeff',
  'processing_time_interaction_coeff',
  'slack_feedback_webhook_url',
];

const Settings = () => {
  const { apiCall } = useApi();
  const { environment, environments } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [editValues, setEditValues] = useState({});
  const [showDbConfirm, setShowDbConfirm] = useState(false);
  // Multi-stage DB save progress: null | 'saving' | 'reconnecting' | 'verifying' | 'success' | 'error'
  const [dbStage, setDbStage] = useState(null);
  const [dbResult, setDbResult] = useState(null);

  const envLabel = environments?.[environment]?.label || environment;
  const [livePool, setLivePool] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const [data, health] = await Promise.all([
        apiCall('/admin/settings'),
        apiCall('/admin/db/health').catch(() => null),
      ]);
      setSettings(data);
      setEditValues(data);
      setLivePool(health);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Rewrites the input (and our cached "settings" baseline) to whatever the live pool is
  // actually running on. Used on any DB-save failure so the UI stops showing the bad value.
  const revertInputToLivePool = (health) => {
    if (!health) return;
    setLivePool(health);
    const live = health.active_secret || '';
    setSettings((prev) => (prev ? { ...prev, database_url_secret: live } : prev));
    setEditValues((prev) => ({ ...prev, database_url_secret: live }));
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dbSecret = editValues.database_url_secret || '';
  const dbSecretChanged = settings && (editValues.database_url_secret || '') !== (settings.database_url_secret || '');
  // Only block save when the user is actively changing the DB secret to something invalid.
  // If the field was already blank from the backend and they're editing unrelated fields, don't lock them out.
  const dbSecretInvalid = dbSecretChanged && (!dbSecret.trim() || dbSecret.length > 512);

  const flashSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const performSave = async () => {
    setSaving(true);
    setError('');
    setDbResult(null);

    const updateData = {};
    SETTINGS_KEYS.forEach((key) => {
      if (editValues[key] !== settings[key]) updateData[key] = editValues[key];
    });

    if (Object.keys(updateData).length === 0) {
      setSaving(false);
      return;
    }

    const dbChanged = 'database_url_secret' in updateData;
    const expectedSecret = updateData.database_url_secret;

    if (!dbChanged) {
      // Fast path: no DB change, no reconnect, no verification.
      try {
        const data = await apiCall('/admin/settings', {
          method: 'PUT',
          body: JSON.stringify(updateData),
        });
        setSettings(data);
        setEditValues(data);
        flashSuccess('Settings saved');
      } catch (err) {
        setError(err.message);
      }
      setSaving(false);
      return;
    }

    // DB-changing path: stage-by-stage with explicit verification.
    setDbStage('saving');
    try {
      const putStart = performance.now();
      const data = await apiCall('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      const putMs = Math.round(performance.now() - putStart);
      setSettings(data);
      setEditValues(data);

      // Reconnect: backend now probes SELECT 1 before swapping pools.
      // Bad secret / unreachable DB → 500 with a descriptive detail; pool untouched.
      // Success → returns { secret, host, database } of the now-live pool.
      setDbStage('reconnecting');
      const reconnectStart = performance.now();
      let reconnectData;
      try {
        reconnectData = await apiCall('/admin/db/reconnect', { method: 'POST' });
      } catch (reconnectErr) {
        // Reconnect failed — backend kept the old pool. Re-sync form to whatever
        // the live pool is actually using so the user isn't staring at their bad input.
        const liveHealth = await apiCall('/admin/db/health').catch(() => null);
        if (liveHealth) revertInputToLivePool(liveHealth);
        setDbStage('error');
        setDbResult({
          failedStage: 'reconnect',
          secret: expectedSecret,
          health: liveHealth,
          message: reconnectErr.message,
        });
        setSaving(false);
        return;
      }
      const reconnectMs = Math.round(performance.now() - reconnectStart);

      // Verify against live pool metadata. /admin/db/health returns the active_secret
      // currently backing the pool plus a fresh SELECT 1 probe. Two checks:
      //   1. probe_ok must be true (the live pool actually runs a query).
      //   2. active_secret must equal the secret we just saved (catches any half-swap).
      setDbStage('verifying');
      const verifyStart = performance.now();
      try {
        const health = await apiCall('/admin/db/health');
        const verifyMs = Math.round(performance.now() - verifyStart);

        if (health.probe_ok === false) {
          revertInputToLivePool(health);
          setDbStage('error');
          setDbResult({
            failedStage: 'verify',
            secret: expectedSecret,
            health,
            message: `Live pool probe failed: ${health.error || 'unknown error'}. The backend reports the new secret is active but SELECT 1 is failing against it.`,
          });
          setSaving(false);
          return;
        }

        if ((health.active_secret || '') !== (expectedSecret || '')) {
          revertInputToLivePool(health);
          setDbStage('error');
          setDbResult({
            failedStage: 'verify',
            secret: expectedSecret,
            health,
            message: `Mismatch: you saved "${expectedSecret}" but the live pool is running on "${health.active_secret || '(env fallback)'}". Backend fell back to a resilient default — check logs for why the new secret did not resolve.`,
          });
          setSaving(false);
          return;
        }

        // Refresh form state from the backend.
        const fresh = await apiCall('/admin/settings');
        setSettings(fresh);
        setEditValues(fresh);
        setLivePool(health);

        setDbStage('success');
        setDbResult({
          secret: expectedSecret,
          putMs,
          reconnectMs,
          verifyMs,
          health,
          reconnectData,
        });
      } catch (verifyErr) {
        // Couldn't reach /admin/db/health — no way to know truth. Fall back to settings re-read.
        const fresh = await apiCall('/admin/settings').catch(() => null);
        if (fresh) {
          setSettings(fresh);
          setEditValues(fresh);
        }
        setDbStage('error');
        setDbResult({
          failedStage: 'verify',
          secret: expectedSecret,
          message: `GET /admin/db/health failed: ${verifyErr.message}. Cannot confirm whether the pool is healthy. Check backend logs.`,
        });
      }
    } catch (err) {
      setDbStage('error');
      setDbResult({ failedStage: 'save', secret: expectedSecret, message: err.message });
    }
    setSaving(false);
  };

  const dismissDbResult = () => {
    setDbStage(null);
    setDbResult(null);
  };

  const handleSave = async () => {
    if (dbSecretInvalid) return;
    if (dbSecretChanged) {
      setShowDbConfirm(true);
      return;
    }
    await performSave();
  };

  const handleConfirmDbSave = async () => {
    setShowDbConfirm(false);
    await performSave();
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all settings to defaults?')) return;
    setSaving(true);
    try {
      await apiCall('/admin/settings/reset', { method: 'POST' });
      await fetchSettings();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleChange = (key, value) => {
    setEditValues({ ...editValues, [key]: value });
  };

  if (loading) return <Loader />;

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2 className="page-title">Settings</h2>
        <div className="header-actions">
          <button onClick={handleReset} disabled={saving} className="btn btn-secondary">
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving || dbSecretInvalid}
            className="btn btn-primary"
            title={dbSecretInvalid ? 'Database URL Secret cannot be blank when changed' : undefined}
          >
            {dbStage === 'saving' && 'Saving...'}
            {dbStage === 'reconnecting' && 'Reconnecting DB...'}
            {dbStage === 'verifying' && 'Verifying...'}
            {!dbStage && saving && 'Saving...'}
            {!saving && 'Save'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMsg && <div className="success-message">{successMsg}</div>}

      {!dbStage && livePool && settings && (livePool.active_secret || '') !== (settings.database_url_secret || '') && (
        <div className="db-warning-card">
          <div className="db-card-header">
            <span className="db-card-icon db-card-icon-warning">!</span>
            <div>
              <div className="db-card-title">Stored secret does not match the live pool</div>
              <div className="db-card-subtitle">
                The settings row says <code>{settings.database_url_secret || '(empty)'}</code> but the backend is actually running on{' '}
                {livePool.active_secret
                  ? <code>{livePool.active_secret}</code>
                  : <>env fallback — <code>{livePool.database || '?'}</code> on <code>{livePool.host || '?'}</code></>}
                . Likely the stored secret could not be resolved and the backend fell back to <code>DATABASE_URL</code>.
              </div>
            </div>
          </div>
          <div className="db-error-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setEditValues((prev) => ({ ...prev, database_url_secret: livePool.active_secret || '' }))}
            >
              Replace input with live value
            </button>
          </div>
        </div>
      )}

      {(dbStage === 'saving' || dbStage === 'reconnecting' || dbStage === 'verifying') && (
        <div className="db-progress-card">
          <div className="db-progress-title">Updating database connection for {envLabel}</div>
          <ol className="db-progress-steps">
            <li className={dbStage === 'saving' ? 'active' : 'done'}>
              {dbStage === 'saving' ? '⏳' : '✓'} Save settings
            </li>
            <li className={dbStage === 'reconnecting' ? 'active' : (dbStage === 'verifying' || dbStage === 'success' ? 'done' : 'pending')}>
              {dbStage === 'reconnecting' ? '⏳' : (dbStage === 'verifying' || dbStage === 'success' ? '✓' : '○')} Reconnect backend to new DB
            </li>
            <li className={dbStage === 'verifying' ? 'active' : (dbStage === 'success' ? 'done' : 'pending')}>
              {dbStage === 'verifying' ? '⏳' : (dbStage === 'success' ? '✓' : '○')} Confirm live pool (GET /admin/db/health)
            </li>
          </ol>
        </div>
      )}

      {dbStage === 'success' && dbResult && (
        <div className="db-success-card">
          <div className="db-card-header">
            <span className="db-card-icon">✓</span>
            <div>
              <div className="db-card-title">Database connected and verified</div>
              <div className="db-card-subtitle">
                {envLabel} backend live pool is running on secret <code>{dbResult.health?.active_secret || dbResult.secret}</code>
              </div>
            </div>
            <button className="btn-close" onClick={dismissDbResult} title="Dismiss">×</button>
          </div>
          {dbResult.health && (
            <div className="db-pool-meta">
              <div><span className="db-meta-label">Host:</span> <code>{dbResult.health.host || '—'}</code></div>
              <div><span className="db-meta-label">Database:</span> <code>{dbResult.health.database || '—'}</code></div>
              <div><span className="db-meta-label">SELECT 1 probe:</span> {dbResult.health.probe_latency_ms ?? '—'}ms</div>
            </div>
          )}
          <ol className="db-progress-steps">
            <li className="done">✓ Settings saved <span className="db-timing">({dbResult.putMs}ms)</span></li>
            <li className="done">✓ Backend reconnected + probe passed <span className="db-timing">({dbResult.reconnectMs}ms)</span></li>
            <li className="done">
              ✓ Live pool reports <code>active_secret</code> matches the saved value
              <span className="db-timing"> ({dbResult.verifyMs}ms)</span>
            </li>
          </ol>
        </div>
      )}

      {dbStage === 'error' && dbResult && (
        <div className="db-error-card">
          <div className="db-card-header">
            <span className="db-card-icon db-card-icon-error">✕</span>
            <div>
              <div className="db-card-title">
                {dbResult.failedStage === 'save' && 'Failed to save settings'}
                {dbResult.failedStage === 'reconnect' && 'Reconnect failed'}
                {dbResult.failedStage === 'verify' && 'Verification failed — connection not confirmed'}
              </div>
              <div className="db-card-subtitle">
                Attempted secret: <code>{dbResult.secret}</code>
              </div>
            </div>
            <button className="btn-close" onClick={dismissDbResult} title="Dismiss">×</button>
          </div>
          <div className="db-error-detail">{dbResult.message}</div>
          {dbResult.health && (
            <div className="db-pool-meta">
              <div>
                <span className="db-meta-label">Live pool active_secret:</span>{' '}
                {dbResult.health.active_secret
                  ? <code>{dbResult.health.active_secret}</code>
                  : <>env fallback — <code>{dbResult.health.database || '?'}</code> on <code>{dbResult.health.host || '?'}</code></>}
              </div>
              <div><span className="db-meta-label">Host:</span> <code>{dbResult.health.host || '—'}</code></div>
              <div><span className="db-meta-label">Database:</span> <code>{dbResult.health.database || '—'}</code></div>
            </div>
          )}
          <div className="db-error-actions">
            <p className="db-error-hint">
              {dbResult.failedStage === 'reconnect' && 'Reconnect refused the new secret. The live pool is unchanged (backend probes SELECT 1 before swapping). Fix the secret above and save again.'}
              {dbResult.failedStage === 'verify' && 'Reconnect reported success but the live pool does not match or cannot run SELECT 1. Check backend logs — this should not happen with the new swap_engine().'}
              {dbResult.failedStage === 'save' && 'The PUT itself failed — nothing was persisted. Safe to retry.'}
            </p>
          </div>
        </div>
      )}

      <div className="settings-grid">
        <div className="settings-card">
          <h3 className="card-title">API Configuration</h3>
          <div className="form-fields">
            <div className="form-group">
              <label className="form-label">Database URL Secret</label>
              <input
                type="text"
                value={editValues.database_url_secret || ''}
                onChange={(e) => handleChange('database_url_secret', e.target.value)}
                className="form-input"
                placeholder="dev-qarl-studio-db-url"
                maxLength={512}
              />
              <p className="form-helper">
                Secret Manager secret name within the current GCP project (not a raw connection string). Changing this will disconnect the backend from the current DB until reconnect completes.
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">Video Generator API URL</label>
              <input
                type="text"
                value={editValues.video_generator_api_url || ''}
                onChange={(e) => handleChange('video_generator_api_url', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Voice API URL</label>
              <input
                type="text"
                value={editValues.voice_api_url || ''}
                onChange={(e) => handleChange('voice_api_url', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">API Timeout (seconds)</label>
              <input
                type="number"
                value={editValues.video_generator_timeout || ''}
                onChange={(e) => handleChange('video_generator_timeout', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">GCP Bucket Name</label>
              <input
                type="text"
                value={editValues.gcp_bucket_name || ''}
                onChange={(e) => handleChange('gcp_bucket_name', e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Slack Feedback Webhook URL</label>
              <input
                type="text"
                value={editValues.slack_feedback_webhook_url || ''}
                onChange={(e) => handleChange('slack_feedback_webhook_url', e.target.value)}
                className="form-input"
                placeholder="https://hooks.slack.com/..."
              />
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="card-title">Processing Settings</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Default Processing Time (s)</label>
              <input
                type="number"
                value={editValues.default_video_processing_time || ''}
                onChange={(e) => handleChange('default_video_processing_time', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Max Retries</label>
              <input
                type="number"
                value={editValues.max_retries || ''}
                onChange={(e) => handleChange('max_retries', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Retry Delay (s)</label>
              <input
                type="number"
                value={editValues.retry_delay || ''}
                disabled
                className="form-input form-input-disabled"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Default Priority (0-9)</label>
              <input
                type="number"
                min="0"
                max="9"
                value={editValues.default_priority ?? 0}
                onChange={(e) => handleChange('default_priority', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="card-title">Credit Formula</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Slope</label>
              <input
                type="number"
                step="any"
                value={editValues.credit_formula_slope ?? ''}
                onChange={(e) => handleChange('credit_formula_slope', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Intercept</label>
              <input
                type="number"
                step="any"
                value={editValues.credit_formula_intercept ?? ''}
                onChange={(e) => handleChange('credit_formula_intercept', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
          </div>
        </div>

        <div className="settings-card">
          <h3 className="card-title">Processing Time Formula</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Intercept</label>
              <input
                type="number"
                step="any"
                value={editValues.processing_time_intercept ?? ''}
                onChange={(e) => handleChange('processing_time_intercept', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Text Length Coeff</label>
              <input
                type="number"
                step="any"
                value={editValues.processing_time_text_len_coeff ?? ''}
                onChange={(e) => handleChange('processing_time_text_len_coeff', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Sampling TS Coeff</label>
              <input
                type="number"
                step="any"
                value={editValues.processing_time_sampling_ts_coeff ?? ''}
                onChange={(e) => handleChange('processing_time_sampling_ts_coeff', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Interaction Coeff</label>
              <input
                type="number"
                step="any"
                value={editValues.processing_time_interaction_coeff ?? ''}
                onChange={(e) => handleChange('processing_time_interaction_coeff', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
          </div>
        </div>
      </div>

      {showDbConfirm && (
        <div className="modal-overlay" onClick={() => !saving && setShowDbConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Change Database URL Secret for {envLabel}?</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                You're about to change the Database URL Secret for the <strong>{envLabel}</strong> environment. The backend will disconnect from the current DB and reconnect to the one referenced by the new secret.
              </p>
              <p className="confirm-subtext">Typos here break every API call.</p>
            </div>
            <div className="modal-footer modal-footer-right">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDbConfirm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmDbSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
